/**
 * Infrastructure Runbook Template Form
 * Operations procedures and playbooks
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const RunbookForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    runbook_name: '', version: '1.0', date: new Date().toISOString().split('T')[0], prepared_by: '', reviewed_by: '',
    purpose: '', scope: '', target_audience: '', system_name: '', environment: '',
    dependencies: [''], contacts: [{ name: '', role: '', email: '', phone: '' }],
    sections: [{ title: '', description: '', prerequisites: [''], steps: [{ step_number: 1, action: '', command: '', expected_output: '', rollback: '' }], troubleshooting: [] }],
    emergency_contacts: [{ name: '', role: '', phone: '' }], escalation_path: [''],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    runbook_name: 'Database Failover Procedure',
    version: '2.1',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'SRE Team',
    reviewed_by: 'DBA Lead',
    purpose: 'This runbook provides step-by-step instructions for performing a manual database failover from primary to replica in case of primary database failure.',
    scope: 'Covers PostgreSQL primary/replica failover for production environment. Does not cover automatic failover (handled by Patroni).',
    target_audience: 'SRE Team, DBAs, On-call Engineers',
    system_name: 'PostgreSQL Production Cluster',
    environment: 'Production (AWS us-east-1)',
    dependencies: ['PostgreSQL 14+', 'Patroni 3.0+', 'etcd cluster', 'AWS CLI configured', 'VPN access'],
    contacts: [
      { name: 'John Smith', role: 'SRE Lead', email: 'john@company.com', phone: '+1-555-0101' },
      { name: 'Mike Wilson', role: 'DBA', email: 'mike@company.com', phone: '+1-555-0102' }
    ],
    sections: [
      {
        title: 'Pre-Failover Checks',
        description: 'Verify system state before initiating failover',
        prerequisites: ['VPN connected', 'SSH access to database servers', 'AWS CLI configured'],
        steps: [
          { step_number: 1, action: 'Check current cluster status', command: 'patronictl -c /etc/patroni/patroni.yml list', expected_output: 'Shows current leader and replicas', rollback: 'N/A' },
          { step_number: 2, action: 'Verify replica lag', command: 'SELECT pg_wal_lsn_diff(pg_current_wal_lsn(), replay_lsn) FROM pg_stat_replication;', expected_output: 'Lag should be < 1MB', rollback: 'If lag > 10MB, wait or investigate' },
          { step_number: 3, action: 'Check disk space on replica', command: 'df -h /var/lib/postgresql', expected_output: '> 20% free space', rollback: 'Clean up if needed' }
        ],
        troubleshooting: [{ issue: 'Cannot connect to database', solution: 'Check VPN connection and security groups' }]
      },
      {
        title: 'Execute Failover',
        description: 'Promote replica to primary',
        prerequisites: ['Pre-failover checks completed', 'Stakeholders notified'],
        steps: [
          { step_number: 1, action: 'Initiate switchover via Patroni', command: 'patronictl -c /etc/patroni/patroni.yml switchover --master db-primary --candidate db-replica', expected_output: 'Confirmation prompt', rollback: 'Cancel if prompted' },
          { step_number: 2, action: 'Confirm switchover', command: 'y', expected_output: 'Successfully switched over', rollback: 'See emergency rollback section' },
          { step_number: 3, action: 'Verify new leader', command: 'patronictl -c /etc/patroni/patroni.yml list', expected_output: 'db-replica shows as Leader', rollback: 'Manual intervention required' }
        ],
        troubleshooting: [{ issue: 'Switchover fails', solution: 'Check Patroni logs: journalctl -u patroni -n 100' }]
      },
      {
        title: 'Post-Failover Verification',
        description: 'Verify application connectivity and health',
        prerequisites: ['Failover completed'],
        steps: [
          { step_number: 1, action: 'Test application connectivity', command: 'curl -s https://api.company.com/health | jq .database', expected_output: '"healthy"', rollback: 'Check connection strings' },
          { step_number: 2, action: 'Verify replication to new replica', command: 'SELECT client_addr, state FROM pg_stat_replication;', expected_output: 'Shows streaming connection', rollback: 'Restart old primary as replica' },
          { step_number: 3, action: 'Monitor error rates', command: 'Check Grafana dashboard: Database Errors', expected_output: 'No spike in errors', rollback: 'Investigate application logs' }
        ],
        troubleshooting: []
      }
    ],
    emergency_contacts: [
      { name: 'On-Call SRE', role: 'Primary', phone: 'PagerDuty: SRE-ONCALL' },
      { name: 'DBA Team', role: 'Escalation', phone: 'PagerDuty: DBA-ONCALL' },
      { name: 'VP Engineering', role: 'Executive', phone: '+1-555-0199' }
    ],
    escalation_path: ['On-Call SRE', 'SRE Lead', 'DBA Lead', 'VP Engineering', 'CTO'],
    rai_assignments: [
      { task: 'Review and update runbook quarterly', responsible: 'SRE Team', accountable: 'SRE Lead', informed: 'DBA Team', due_date: '2025-04-01', status: 'Pending' }
    ],
    followups: []
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Infrastructure Runbook Draft";
      const payload = {
        name: draftName,
        template_type: "Infrastructure Runbook",
        template_id: "runbook",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "runbook", "Runbook", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("runbook", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/runbook`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Runbook_${formData.runbook_name.replace(/\s+/g, '_') || 'Document'}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Infrastructure Runbook",
        formData,
        settings: activeSettings || {},
        templateType: "Infrastructure Runbook",
        templateId: "runbook",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, { title: '', description: '', prerequisites: [''], steps: [{ step_number: 1, action: '', command: '', expected_output: '', rollback: '' }], troubleshooting: [] }]
    });
  };

  const updateSection = (index, field, value) => {
    const sections = [...formData.sections];
    sections[index] = { ...sections[index], [field]: value };
    setFormData({ ...formData, sections });
  };

  const addStepToSection = (sectionIndex) => {
    const sections = [...formData.sections];
    const nextStep = sections[sectionIndex].steps.length + 1;
    sections[sectionIndex].steps.push({ step_number: nextStep, action: '', command: '', expected_output: '', rollback: '' });
    setFormData({ ...formData, sections });
  };

  const updateStep = (sectionIndex, stepIndex, field, value) => {
    const sections = [...formData.sections];
    sections[sectionIndex].steps[stepIndex] = { ...sections[sectionIndex].steps[stepIndex], [field]: value };
    setFormData({ ...formData, sections });
  };

  const removeStep = (sectionIndex, stepIndex) => {
    const sections = [...formData.sections];
    sections[sectionIndex].steps = sections[sectionIndex].steps.filter((_, i) => i !== stepIndex);
    setFormData({ ...formData, sections });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Infrastructure Runbook</h1><p className="text-sm text-gray-500">Operations procedures and playbooks</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Runbook Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Runbook Name *</Label><Input value={formData.runbook_name} onChange={(e) => setFormData({...formData, runbook_name: e.target.value})} placeholder="Database Failover Procedure" /></div>
              <div><Label>Version</Label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="1.0" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="SRE Team" /></div>
              <div><Label>Reviewed By</Label><Input value={formData.reviewed_by} onChange={(e) => setFormData({...formData, reviewed_by: e.target.value})} placeholder="Tech Lead" /></div>
              <div><Label>System Name</Label><Input value={formData.system_name} onChange={(e) => setFormData({...formData, system_name: e.target.value})} placeholder="PostgreSQL Cluster" /></div>
              <div><Label>Environment</Label><Input value={formData.environment} onChange={(e) => setFormData({...formData, environment: e.target.value})} placeholder="Production" /></div>
            </div>

            <div><Label>Purpose</Label><Textarea value={formData.purpose} onChange={(e) => setFormData({...formData, purpose: e.target.value})} placeholder="What this runbook helps accomplish..." rows={2} /></div>
            <div><Label>Scope</Label><Textarea value={formData.scope} onChange={(e) => setFormData({...formData, scope: e.target.value})} placeholder="What is covered and what is not..." rows={2} /></div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Procedure Sections</Label>
              {formData.sections.map((section, sIdx) => (
                <div key={sIdx} className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-sm font-medium text-blue-800">Section #{sIdx + 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, sections: formData.sections.filter((_, i) => i !== sIdx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div><Label className="text-xs">Section Title</Label><Input value={section.title} onChange={(e) => updateSection(sIdx, 'title', e.target.value)} placeholder="Pre-Failover Checks" className="text-sm" /></div>
                    <div><Label className="text-xs">Description</Label><Textarea value={section.description} onChange={(e) => updateSection(sIdx, 'description', e.target.value)} placeholder="What this section covers..." rows={2} className="text-sm" /></div>
                  </div>

                  <div className="mt-3">
                    <Label className="text-xs font-medium">Steps</Label>
                    {section.steps.map((step, stepIdx) => (
                      <div key={stepIdx} className="mt-2 p-2 bg-white rounded border">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                          <div><Label className="text-[10px]">Step #</Label><Input type="number" value={step.step_number} onChange={(e) => updateStep(sIdx, stepIdx, 'step_number', parseInt(e.target.value) || 0)} className="text-xs h-8" /></div>
                          <div className="md:col-span-2"><Label className="text-[10px]">Action</Label><Input value={step.action} onChange={(e) => updateStep(sIdx, stepIdx, 'action', e.target.value)} placeholder="What to do" className="text-xs h-8" /></div>
                          <div className="md:col-span-2"><Label className="text-[10px]">Command</Label><Input value={step.command} onChange={(e) => updateStep(sIdx, stepIdx, 'command', e.target.value)} placeholder="Command to run" className="text-xs h-8 font-mono" /></div>
                          <div className="flex items-end"><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeStep(sIdx, stepIdx)}><Trash2 className="h-3 w-3 text-red-500" /></Button></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                          <div><Label className="text-[10px]">Expected Output</Label><Input value={step.expected_output} onChange={(e) => updateStep(sIdx, stepIdx, 'expected_output', e.target.value)} placeholder="What you should see" className="text-xs h-8" /></div>
                          <div><Label className="text-[10px]">Rollback</Label><Input value={step.rollback} onChange={(e) => updateStep(sIdx, stepIdx, 'rollback', e.target.value)} placeholder="How to undo" className="text-xs h-8" /></div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addStepToSection(sIdx)} className="mt-2"><Plus className="h-3 w-3 mr-1" /> Add Step</Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addSection} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Section</Button>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Escalation Path</Label>
              {formData.escalation_path.map((item, idx) => (
                <div key={idx} className="flex gap-2 mt-1 items-center">
                  <span className="text-sm text-gray-500 w-6">{idx + 1}.</span>
                  <Input value={item} onChange={(e) => { const path = [...formData.escalation_path]; path[idx] = e.target.value; setFormData({...formData, escalation_path: path}); }} placeholder="Role/Person" />
                  <Button variant="ghost" size="icon" onClick={() => setFormData({...formData, escalation_path: formData.escalation_path.filter((_, i) => i !== idx)})}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFormData({...formData, escalation_path: [...formData.escalation_path, '']})} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RunbookForm;
